import { ENDPOINTS } from "../../shared/constants/urls";
import { apiSlice } from "./apiSlice";

export const profileApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    analyzeWritingStyle: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_WRITING_STYLE}analyze/auto/`,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    createWritingStyle: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_WRITING_STYLE}analyze/manual/`,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    analyzeCompany: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_COMPANY_VARIABLE}analyze/auto/`,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    createCompany: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_COMPANY_VARIABLE}analyze/manual/`,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    writingStyle: builder.mutation({
      query: (data) => ({
        url: ENDPOINTS.PROFILE_WRITING_STYLE,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    companyVariable: builder.mutation({
      query: (data) => ({
        url: ENDPOINTS.PROFILE_COMPANY_VARIABLE,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    productVariable: builder.mutation({
      query: (data) => ({
        url: ENDPOINTS.PROFILE_PRODUCT_VARIABLE,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    getAllProductVariable: builder.query({
      query: ({ website_url } = {}) => ({
        url: `${ENDPOINTS.PROFILE_PRODUCT_VARIABLE}${
          website_url ? "?website_id=" + website_url : ""
        }`,
      }),
      providesTags: ["Product"],
      keepUnusedDataFor: 5,
      extraOptions: { requiresAuth: true },
    }),
    getAllWritingStyles: builder.query({
      query: ({ website_url } = {}) => ({
        url: `${ENDPOINTS.PROFILE_WRITING_STYLE}${
          website_url ? "?website_id=" + website_url : ""
        }`,
      }),
      providesTags: ["Writing"],
      keepUnusedDataFor: 5,
      extraOptions: { requiresAuth: true },
    }),
    getAllCompanyVariables: builder.query({
      query: ({ website_url } = {}) => ({
        url: `${ENDPOINTS.PROFILE_COMPANY_VARIABLE}${
          website_url ? "?website_id=" + website_url : ""
        }`,
      }),
      providesTags: ["Company"],
      keepUnusedDataFor: 5,
      extraOptions: { requiresAuth: true },
    }),
    deleteWritingStyle: builder.mutation({
      query: (id) => ({
        url: `${ENDPOINTS.PROFILE_WRITING_STYLE}${id}`,
        method: "DELETE",
      }),
      extraOptions: { requiresAuth: true },
    }),
    deleteSelectedWritingStyle: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_WRITING_STYLE}selection/`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json", // Ensure this header is present
        },
        body: JSON.stringify(data), // Make sure the body is correctly serialized as JSON
      }),
      extraOptions: { requiresAuth: true },
    }),
    deleteCompanyVariable: builder.mutation({
      query: (id) => ({
        url: `${ENDPOINTS.PROFILE_COMPANY_VARIABLE}${id}`,
        method: "DELETE",
      }),
      extraOptions: { requiresAuth: true },
    }),
    deleteSelectedCompanyVariable: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_COMPANY_VARIABLE}selection/`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json", // Ensure this header is present
        },
        body: JSON.stringify(data), // Make sure the body is correctly serialized as JSON
      }),
      extraOptions: { requiresAuth: true },
    }),
    deleteProductVariable: builder.mutation({
      query: (id) => ({
        url: `${ENDPOINTS.PROFILE_PRODUCT_VARIABLE}${id}`,
        method: "DELETE",
      }),
      extraOptions: { requiresAuth: true },
    }),
    deleteSelectedProductVariable: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_PRODUCT_VARIABLE}selection/`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json", // Ensure this header is present
        },
        body: JSON.stringify(data), // Make sure the body is correctly serialized as JSON
      }),
      extraOptions: { requiresAuth: true },
    }),
    changeEmail: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.AUTH_CHANGE_EMAIL}`,
        method: "PATCH",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    changeEmailVerification: builder.mutation({
      query: (token) => ({
        url: `${ENDPOINTS.AUTH_CHANGE_EMAIL_VERIFICATION}${token}/`,
        method: "POST",
      }),
      extraOptions: { requiresAuth: true },
    }),
    updateProfile: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.AUTH_UPDATE_PROFILE}`,
        method: "PATCH",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    updatePassword: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.AUTH_CHANGE_PASSWORD}`,
        method: "PATCH",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    getSingleCompany: builder.mutation({
      query: ({ id }) => ({
        url: `${ENDPOINTS.PROFILE_COMPANY_VARIABLE}${id}`,
        method: "GET",
      }),
      providesTags: ["Company"],
      keepUnusedDataFor: 5,
      extraOptions: { requiresAuth: true },
    }),
    updateSingleCompany: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_COMPANY_VARIABLE}${data.id}`,
        method: "PUT",
        body: data,
      }),
      providesTags: ["Company"],
      keepUnusedDataFor: 5,
      extraOptions: { requiresAuth: true },
    }),
    linkSingleCompanyToWebsite: builder.mutation({
      query: ({ projectId, variableId }) => ({
        url: `${ENDPOINTS.PROFILE_COMPANY_VARIABLE}${variableId}/website/${projectId}`,
        method: "PUT",
      }),
      extraOptions: { requiresAuth: true },
    }),
    getSingleWritingStyle: builder.mutation({
      query: ({ id }) => ({
        url: `${ENDPOINTS.PROFILE_WRITING_STYLE}${id}`,
        method: "GET",
      }),
      providesTags: ["Writing"],
      keepUnusedDataFor: 5,
      extraOptions: { requiresAuth: true },
    }),
    updateSingleWritingStyle: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_WRITING_STYLE}${data.id}`,
        method: "PUT",
        body: data,
      }),
      providesTags: ["Writing"],
      keepUnusedDataFor: 5,
      extraOptions: { requiresAuth: true },
    }),
    linkSingleWritingStyleToWebsite: builder.mutation({
      query: ({ projectId, variableId }) => ({
        url: `${ENDPOINTS.PROFILE_WRITING_STYLE}${variableId}/website/${projectId}`,
        method: "PUT",
      }),
      extraOptions: { requiresAuth: true },
    }),
    getSingleProduct: builder.mutation({
      query: ({ id }) => ({
        url: `${ENDPOINTS.PROFILE_PRODUCT_VARIABLE}${id}`,
        method: "GET",
      }),
      providesTags: ["Product"],
      keepUnusedDataFor: 5,
      extraOptions: { requiresAuth: true },
    }),
    updateSingleProduct: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.PROFILE_PRODUCT_VARIABLE}${data.id}`,
        method: "PUT",
        body: data,
      }),
      providesTags: ["Product"],
      keepUnusedDataFor: 5,
      extraOptions: { requiresAuth: true },
    }),
    linkSingleProductToWebsite: builder.mutation({
      query: ({ projectId, variableId }) => ({
        url: `${ENDPOINTS.PROFILE_PRODUCT_VARIABLE}${variableId}/website/${projectId}`,
        method: "PUT",
      }),
      extraOptions: { requiresAuth: true },
    }),
    paymentSuccess: builder.mutation({
      query: (data) => ({
        url: ENDPOINTS.PAYMENT_SUCCESS,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    linkOpenAI: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.AUTH_LINK_OPENAI}`,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    unlinkOpenAI: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.AUTH_UNLINK_OPENAI}`,
        method: "DELETE",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    linkPerplexity: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.AUTH_LINK_PERPLEXITY}`,
        method: "POST",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
    unlinkPerplexity: builder.mutation({
      query: (data) => ({
        url: `${ENDPOINTS.AUTH_UNLINK_PERPLEXITY}`,
        method: "DELETE",
        body: data,
      }),
      extraOptions: { requiresAuth: true },
    }),
  }),
});

export const {
  useAnalyzeWritingStyleMutation,
  useCreateWritingStyleMutation,
  useAnalyzeCompanyMutation,
  useCreateCompanyMutation,
  useWritingStyleMutation,
  useCompanyVariableMutation,
  useProductVariableMutation,
  useGetAllWritingStylesQuery,
  useDeleteWritingStyleMutation,
  useDeleteSelectedWritingStyleMutation,
  useDeleteCompanyVariableMutation,
  useDeleteSelectedCompanyVariableMutation,
  useGetAllCompanyVariablesQuery,
  useGetAllProductVariableQuery,
  useDeleteProductVariableMutation,
  useDeleteSelectedProductVariableMutation,
  useUpdateProfileMutation,
  useGetSingleCompanyMutation,
  useUpdateSingleCompanyMutation,
  useLinkSingleCompanyToWebsiteMutation,
  useGetSingleProductMutation,
  useUpdateSingleProductMutation,
  useLinkSingleProductToWebsiteMutation,
  useGetSingleWritingStyleMutation,
  useUpdateSingleWritingStyleMutation,
  useLinkSingleWritingStyleToWebsiteMutation,
  useUpdatePasswordMutation,
  useChangeEmailMutation,
  useChangeEmailVerificationMutation,
  usePaymentSuccessMutation,
  useLinkOpenAIMutation,
  useUnlinkOpenAIMutation,
  useLinkPerplexityMutation,
  useUnlinkPerplexityMutation,
} = profileApiSlice;
